<template>
	<InstructionPopoverButton
		v-if="!availableCurricula.length"
		:label="$t('Curriculum')"
		:instruction="$t('No curriculum has been set for your school account yet.')"
		:CTAText="$t('Learn more about getting started with a curriculum')"
		@clickCTA="$emit('clickLearnMore')"
	/>

	<div v-else class="relative">
		<div class="join">
			<button type="button" class="btn btn-sm btn-outline join-item" :disabled="pending" @click="openCurrent">
				<span v-if="pending" class="loading loading-spinner loading-xs"></span>
				{{ currentCurriculum.name }}
			</button>

			<button
				v-if="scope === 'global' || availableCurricula.length > 1"
				type="button"
				class="btn btn-sm btn-outline join-item px-2"
				:disabled="pending"
				:aria-label="$t('Choose curriculum')"
				@click="$refs.menu.open()"
			>
				<EllipsisHorizontalIcon class="w-5 h-5" />
			</button>
		</div>

		<Dropdown ref="menu" position="bottom-end" class="w-80" teleport>
			<ul class="menu bg-base-100 rounded-box shadow-lg border border-base-200">
				<template v-for="group in groupedCurricula" :key="group.country">
					<li class="menu-title flex flex-row items-center gap-2">
						<CountryFlag :code="group.country" class="w-4 h-3" />
						{{ group.country }}
					</li>
					<li v-for="curriculum in group.items" :key="keyOf(curriculum)">
						<a @click.prevent="pick(curriculum)">{{ curriculum.menuLabel }}</a>
					</li>
				</template>
			</ul>
		</Dropdown>
	</div>
</template>

<script lang="ts">
import { mapStores } from "pinia";
import { EllipsisHorizontalIcon } from "@heroicons/vue/24/outline";
import Dropdown from "@/components/ui/Dropdown.v2.vue";
import InstructionPopoverButton from "@/components/ui/InstructionPopoverButton.vue";
import { Curriculum } from "@/models/Curriculum.model";
import { useCurriculaStore } from "@/stores/Curricula.store";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import CountryFlag from "@/components/ui/CountryFlag.vue";

export default {
	emits: ['openCurriculum'],

	components: {
		Dropdown,
		InstructionPopoverButton,
		EllipsisHorizontalIcon,
		CountryFlag,
	},

	props: {
		// Which curricula the button offers. 'global' (library): every curriculum of the
		// user's schools. 'school' (agenda, theme weeks, …): only the current school's.
		scope: {
			type: String,
			required: true,
			validator: (value: string) => ['global', 'school'].includes(value),
		},

		// True while a launch fired from this button is still loading its picker — the
		// external pickers can take seconds (chunk fetch + vendor script + widget upgrade),
		// so the button holds a spinner until the picker reports `opened`.
		pending: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			// Component-local: initialised to the selected school's curriculum on
			// mount, not persisted across mounts (spec 2.1).
			selectedKey: null as string | null,
		};
	},

	computed: {
		...mapStores(useCurriculaStore, useOrganisationsStore),

		availableCurricula(): Curriculum[] {
			return this.scope === 'school'
				? this.curriculaStore.availableForCurrentSchool
				: this.curriculaStore.availableForUser;
		},

		currentCurriculum(): Curriculum {
			return this.availableCurricula.find((c) => this.keyOf(c) === this.selectedKey)
				?? this.availableCurricula[0];
		},

		/**
		 * The country the menu leads with: the selected school's own, falling back to the
		 * country of its curriculum (most organisations have no country of their own set).
		 */
		currentCountry(): string | null {
			const organisation = this.organisationsStore.currentOrganisation;
			return organisation?.country ?? organisation?.curriculum?.country ?? null;
		},

		groupedCurricula(): { country: string, items: Curriculum[] }[] {
			const groups = new Map<string, { country: string, items: Curriculum[] }>();
			for (const curriculum of this.availableCurricula) {
				const country = curriculum.country ?? '—';
				if (!groups.has(country)) {
					// Flag comes from the registry (curricula.json), not from a table here.
					groups.set(country, { country, items: [] });
				}
				groups.get(country)!.items.push(curriculum);
			}

			// The selected school's country leads; the rest keep the order they appear in,
			// which is registry order — no hard-coded country ranking.
			return [...groups.values()].sort((a, b) => {
				return Number(b.country === this.currentCountry) - Number(a.country === this.currentCountry);
			});
		},
	},

	// NOTE: initialisation runs in created(), not mounted(). Both satisfy "on
	// mount" semantically (before the user can interact with the button), but
	// created() runs before the first render, so the initial paint already
	// reflects the selected school's curriculum — no extra reactive re-render
	// (and no test-side nextTick/flush) needed.
	created() {
		const schoolCurriculum = this.organisationsStore.currentOrganisation?.curriculum;
		if (schoolCurriculum) {
			const match = this.availableCurricula.find((c) => this.keyOf(c) === this.keyOf(schoolCurriculum));
			if (match) {
				this.selectedKey = this.keyOf(match);
			}
		}
	},

	methods: {
		keyOf(curriculum: Curriculum): string {
			return curriculum.key ?? curriculum.id.toLowerCase();
		},

		openCurrent() {
			this.$emit('openCurriculum', this.currentCurriculum);
		},

		pick(curriculum: Curriculum) {
			// Picking updates the left segment AND opens that selector immediately (one click).
			this.selectedKey = this.keyOf(curriculum);
			this.$refs.menu.close();
			this.$emit('openCurriculum', curriculum);
		},
	},
};
</script>
